import { useMemo, useState } from 'react';
import { LogIn, Search } from 'lucide-react';
import { ReportModal } from '@/pages/GamAdvertisers/components/ReportModal';
import type { NavNextPage } from '@/types/navigation-report';
import { fmtNav } from './format';

interface NextPagesTableProps {
    items: NavNextPage[];
    onOpenAsEntry: (path: string) => void;
}

const PREVIEW = 10;
const TD = 'px-2 py-2 text-right tabular-nums text-foreground/90';

function Rows({ items, onOpenAsEntry }: NextPagesTableProps) {
    return (
        <table className="w-full min-w-[520px] text-xs">
            <thead>
                <tr className="border-b border-border text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                    <th className="px-2 py-2 text-left">Próxima página (P2)</th>
                    <th className="px-2 py-2 text-right">Sessões</th>
                    <th className="px-2 py-2 text-right">% da P1</th>
                    <th className="w-36" />
                </tr>
            </thead>
            <tbody>
                {items.map((row) => (
                    <tr key={row.path} className="border-b border-border/60">
                        <td className="max-w-[360px] px-2 py-2 text-left"><span className="block truncate font-medium text-foreground" title={row.path}>{row.path}</span></td>
                        <td className={`${TD} font-medium text-foreground`}>{fmtNav.num(row.sessions)}</td>
                        <td className={TD}>
                            <span className="flex items-center justify-end gap-2">
                                <span className="h-1.5 w-16 overflow-hidden rounded-full bg-border">
                                    <span className="block h-full rounded-full bg-blue-500" style={{ width: `${Math.min(row.share_pct, 100)}%` }} />
                                </span>
                                <span className="w-12">{fmtNav.pct1(row.share_pct)}</span>
                            </span>
                        </td>
                        <td className="px-2 py-1 text-right">
                            <button type="button" onClick={() => onOpenAsEntry(row.path)} className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground hover:bg-accent hover:text-foreground" title="Abrir esta página como ENTRADA (quem entrou por ela — população diferente desta)">
                                <LogIn className="h-3 w-3" /> abrir como entrada
                            </button>
                        </td>
                    </tr>
                ))}
                {items.length === 0 && (
                    <tr><td colSpan={4} className="px-2 py-6 text-center text-muted-foreground">Nenhuma sessão avançou para uma segunda página</td></tr>
                )}
            </tbody>
        </table>
    );
}

/** Próximas páginas da P1: resumo na tela, lista completa em modal (padrão da casa). */
export function NextPagesTable({ items, onOpenAsEntry }: NextPagesTableProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? items.filter((i) => i.path.toLowerCase().includes(q)) : items;
    }, [items, search]);

    return (
        <div className="rounded-lg border border-border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                    Para onde foram <span className="text-foreground/30">· {fmtNav.num(items.length)} página(s)</span>
                </p>
                {items.length > PREVIEW && (
                    <button type="button" onClick={() => setOpen(true)} className="rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground">
                        Ver todas ({fmtNav.num(items.length)})
                    </button>
                )}
            </div>
            <div className="overflow-x-auto font-mono">
                <Rows items={items.slice(0, PREVIEW)} onOpenAsEntry={onOpenAsEntry} />
            </div>

            <ReportModal open={open} onClose={() => setOpen(false)} title="Próximas páginas (P2)" subtitle="Sessões que, depois desta entrada, viram esta página em seguida">
                <label className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Search className="h-3.5 w-3.5" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar path…" className="h-8 w-64 rounded-md border border-border bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                </label>
                <div className="overflow-x-auto font-mono">
                    <Rows items={filtered} onOpenAsEntry={(p) => { setOpen(false); onOpenAsEntry(p); }} />
                </div>
            </ReportModal>
        </div>
    );
}
